import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
// Custom components
import Card from "components/card/Card";
import IconBox from "components/icons/IconBox";
import {
  IconArrowDown,
  IconDownload,
  IconTranslator,
  IconUpload,
} from "components/icons/Icons";
import _ from "lodash";
import { fetchLanguagesExcerpt } from "network/helper";
import { useState, useEffect, Fragment, useMemo } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useDispatch, useSelector } from "react-redux";
import { requestDownloadDocumentById } from "store/project/reducer";
import { setAlertMessageRequest } from "store/ui/reducer";
import { allUsersSelector } from "store/user/selector";

type RowObj = {
  document_id: string;
  language: string;
  source_lang?: string;
  target_lang?: string;
  document_status_id: string;
  order_id: string;
  file: null;
  origin_file?: any;
  lastest_file?: any;
  translators: any[];
  managers: any[];
  actionCol: string;
  hasChanged: boolean;
  new_file: any;
  translated_file?: any;
  final_file_cot?: any;
  cot?: any;
};
// 3-status workflow aligned with backend canonical IDs (app/Controllers/_Projects.php#811):
// New (1), In Progress (2), Completed (4). Legacy IDs 3 (Ready for Review) and
// 5 (In Revision) are not exposed in PM UI but remain valid for historical data.
const documentStatus = [
  {
    id: 1,
    label: "New",
  },
  {
    id: 2,
    label: "In Progress",
  },
  {
    id: 4,
    label: "Completed",
  },
];

const columnHelper = createColumnHelper<RowObj>();

export default function ManagerProjectDetailTable(props: {
  isFetching: boolean;
  tableData: any;
  onUpdateDocument: Function;
}) {
  const dispatch = useDispatch();
  const { isFetching, tableData, onUpdateDocument } = props;
  const [sorting, setSorting] = useState<SortingState>([]);
  const textColor = useColorModeValue("black", "black");
  const allUsers = useSelector(allUsersSelector);
  const translators = useMemo(() => {
    if (allUsers) {
      return allUsers?.translators || [];
    }
    return [];
  }, [allUsers]);

  const shadow = useColorModeValue(
    "0px 4px 50px rgba(0, 0, 0, 0.1)",
    "0px 4px 50px rgba(0, 0, 0, 0.1)"
  );
  let defaultData = tableData.map((data: any) => ({...data, translatorHasChanged: false}));
  const [data, setData] = useState(() => [...defaultData]);

  const onChangeDocumentStatus = (document_id: string, option: any) => {
    let index = data?.findIndex((it) => it.document_id === document_id);
    if (index > -1) {
      let dumpData = _.cloneDeep(data);
      dumpData[index].document_status_id = option.id;
      dumpData[index].hasChanged = true;
      setData([...dumpData]);
    }
  };

  const onDownloadFiles = (document_id: any, file_name: any) => {
    dispatch(
      requestDownloadDocumentById({
        document_id: document_id,
        file_name: `${file_name}-files.zip`,
      })
    );
  };

  const onAssignTranslator = (document_id: string, translator: any) => {
    let index = data?.findIndex((it) => it.document_id === document_id);
    if (index > -1) {
      let dumpData = _.cloneDeep(data);
      if( dumpData[index].translators.map((item:any) => item.user_id).includes(translator.user_id) ) {
        dumpData[index].translators = dumpData[index].translators.filter( (id: any) => id.user_id !== translator.user_id  );
      } else {
        dumpData[index].translators = [ ...dumpData[index].translators, translator];
      }
      dumpData[index].translatorHasChanged = true;
      dumpData[index].hasChanged = true;
      setData([...dumpData]);
    }
  };

  const onClickSaveDocument = (document_id: string) => {
    let index = data?.findIndex((it) => it.document_id === document_id);
    if (index === -1) return;

    // Guard: cannot mark Completed without a translated file present.
    // Either a freshly attached final_file_cot or an existing lastest_file is required.
    const selectedStatus = Number(data[index].document_status_id);
    const hasFinalFileUpload = Boolean(data[index].final_file_cot && data[index].final_file_cot.length > 0);
    const hasExistingTranslatedFile = Boolean(data[index].lastest_file?.document_file_path);
    if (selectedStatus === 4 && !hasFinalFileUpload && !hasExistingTranslatedFile) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message: "Cannot mark Completed without a translated file. Upload a file or select a different status.",
        })
      );
      return;
    }

    // When PM saves with a final translated file attached, mark as Completed (4 per backend).
    let sendData = data[index].final_file_cot ? { ...data[index], document_status_id: 4 } : data[index];
    if (index > -1) {
      data[index].translatorHasChanged === false ? onUpdateDocument({...sendData, translators: []}) :
        onUpdateDocument(sendData);
    }
  };

  const handleChange = (document_id: any, files: any) => {
    let index = data?.findIndex((it) => it.document_id === document_id);
    if (index > -1) {
      let dumpData = _.cloneDeep(data);
      dumpData[index].final_file_cot = Array.from(files);
      dumpData[index].hasChanged = true;
      setData([...dumpData]);
    }
  };

  const columns = [
    columnHelper.accessor("language", {
      id: "language",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Language
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text
            color={textColor}
            fontSize="14px"
            lineHeight="24px"
            fontWeight="400"
            whiteSpace="nowrap"
          >
            {info.row.original?.source_lang &&
              info.row.original?.target_lang && (
                <Fragment>
                  {fetchLanguagesExcerpt(info.row.original?.source_lang)}
                  <IconTranslator width="15px" height="16px" mx="4px" />
                  {fetchLanguagesExcerpt(info.row.original?.target_lang)}
                </Fragment>
              )}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor("document_status_id", {
      id: "document_status_id",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Status
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Menu>
            <MenuButton
              padding={"10px 10px 10px 15px"}
              backgroundColor="#F3F5F5"
              borderRadius="100px"
            >
              <Flex alignItems={"center"}>
                <Text
                  fontSize={"14px"}
                  lineHeight="24px"
                  fontWeight={"400"}
                  color="black"
                >
                  {info.getValue()
                    ? documentStatus?.find(
                        (it) => it.id === Number(info.getValue())
                      )?.label
                    : "-"}
                </Text>
                <IconBox
                  marginLeft="10px"
                  icon={<IconArrowDown width="24px" height="24px" />}
                ></IconBox>
              </Flex>
            </MenuButton>
            <MenuList
              boxShadow={shadow}
              p="0px"
              mt="10px"
              borderRadius="20px"
              bg={"#FFF"}
              border="none"
            >
              <Flex
                flexDirection="column"
                p="10px 20px"
                fontSize={"14px"}
                lineHeight="24px"
                fontWeight={"400"}
                color="black"
              >
                {documentStatus.map((option, index: number) => (
                  <MenuItem
                    key={index}
                    _hover={{ bg: "none" }}
                    _focus={{ bg: "none" }}
                    borderRadius="8px"
                    padding="15px 0px"
                    onClick={() =>
                      onChangeDocumentStatus(
                        info.row.original.document_id,
                        option
                      )
                    }
                    borderBottom={
                      index < info.getValue().length - 1
                        ? "1px solid #F3F5F5"
                        : ""
                    }
                  >
                    <Text fontSize="sm">{option.label}</Text>
                  </MenuItem>
                ))}
              </Flex>
            </MenuList>
          </Menu>
        </Flex>
      ),
    }),
    columnHelper.accessor("origin_file", {
      id: "origin_file",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Original File
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Flex
            color={textColor}
            fontSize="14px"
            lineHeight="24px"
            fontWeight="400"
            alignItems={"center"}
            padding="5px 5px 5px 10px"
            borderRadius="100px"
            border="1px solid #E9E9E9"
          >
            <Box
              maxW="100px"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              {info.row.original.origin_file ? 
                info.row.original.origin_file?.document_original_file_name : ""
              }
            </Box>
    
            <Flex
              padding="5px 15px"
              background="#F3F5F5"
              borderRadius="20px"
              marginLeft="8px"
              cursor="pointer"
              onClick={() => {
                const document_file_path = info.row.original.origin_file ? info.row.original.origin_file
                  .document_file_path : "";
    
                const document_original_file_name = info.row.original
                  .origin_file ? info.row.original.origin_file.document_original_file_name : "";
    
                const fullUrl =
                  document_file_path?.includes("http://") ||
                  document_file_path?.includes("https://")
                    ? document_file_path
                    : `${process.env.REACT_APP_API_BASE_URL}/${document_file_path}/${document_original_file_name}`;
                const link = document.createElement("a");
                link.href = fullUrl;
                link.target = "_blank";
                link.setAttribute("download", `${document_original_file_name}`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
              }}
              _hover={{
                color: "#2F80ED",
                textDecoration: "underline",
              }}
            >
              <IconBox
                icon={<IconDownload width="24px" height="24px" />}
              ></IconBox>
              <Text
                fontSize={{ sm: "12px", lg: "14px" }}
                lineHeight="24px"
                fontWeight="400"
                color="#2F80ED"
                marginLeft="4px"
              >
                Download
              </Text>
            </Flex>
          </Flex>
        </Flex>
      ),
    }),
    columnHelper.accessor("translated_file", {
      id: "translated_file",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Translated File
        </Text>
      ),
      cell: (info) => {
        return (
          <Flex align="center">
            <Flex
              color={textColor}
              fontSize="14px"
              lineHeight="24px"
              fontWeight="400"
              alignItems={"center"}
              padding="5px 5px 5px 10px"
              borderRadius="100px"
              border="1px solid #E9E9E9"
            >
              <Box
                maxW="100px"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                overflow="hidden"
              >
                { 
                  Number(info.row.original?.document_status_id) >= 3 ? "done" :
                    info.row.original.new_file
                      ? info.row.original.new_file.name
                      : info.row.original.lastest_file
                      ? info.row.original.lastest_file.document_original_file_name
                      : "empty"}
              </Box>
              <Flex
                padding="5px 15px"
                background="#F3F5F5"
                borderRadius="20px"
                marginLeft="8px"
                cursor="pointer"
                onClick={() => {
                  onDownloadFiles(info.row.original.document_id, `${info.row.original.order_id}#document`);
                }}
                _hover={{
                  color: "#2F80ED",
                  textDecoration: "underline",
                }}
              >
                <IconBox
                  icon={<IconDownload width="24px" height="24px" />}
                ></IconBox>
                <Text
                  fontSize={{ sm: "12px", lg: "14px" }}
                  lineHeight="24px"
                  fontWeight="400"
                  color="#2F80ED"
                  marginLeft="4px"
                >
                  Download
                </Text>
              </Flex>
            </Flex>
          </Flex>
        );
      }
    }),
    columnHelper.accessor("final_file_cot", {
      id: "final_file_cot",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Final File with COT
        </Text>
      ),
      cell: (info) => {
        return (
          <Flex align="center">
            <Flex
              color={textColor}
              fontSize="14px"
              lineHeight="24px"
              fontWeight="400"
              alignItems={"center"}
              padding="5px 5px 5px 10px"
              borderRadius="100px"
              border="1px solid #E9E9E9"
            >
              <Box
                maxW="100px"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                overflow="hidden"
              >

                {
                  (info.row.original?.final_file_cot?.length > 0 || info.row.original?.cot)  ? (
                    info.row.original?.final_file_cot?.length > 0 && info.row.original?.final_file_cot[0]?.name ||
                    info.row.original?.cot && info.row.original?.cot?.document_original_file_name
                  ) : "empty"
                } 
              </Box>
  
              <FileUploader
                multiple={true}
                handleChange={(files: File[]) => {
                  handleChange(info.row.original.document_id, files);
                }}
                name="files"
                height="full"
                sx={{
                  marginLeft: "32px",
                }}
              >
                <Flex
                  padding="5px 20px 5px 15px"
                  background="rgba(47, 128, 237, 0.14)"
                  borderRadius="20px"
                  marginLeft="8px"
                  boxSize="fit-content"
                  cursor="pointer"
                  _hover={{
                    color: "#2F80ED",
                    textDecoration: "underline",
                  }}
                >
                  <IconBox
                    icon={<IconUpload width="24px" height="24px" />}
                  ></IconBox>
                  <Text
                    fontSize={{ sm: "12px", lg: "14px" }}
                    lineHeight="24px"
                    fontWeight="400"
                    color="#2F80ED"
                    marginLeft="4px"
                  >
                    Upload
                  </Text>
                </Flex>
              </FileUploader>
            </Flex>
          </Flex>
        );
      }
    }),

    columnHelper.accessor("translators", {
      id: "translators",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          color="gray.500"
          fontWeight="400"
        >
          Translator
        </Text>
      ),
      cell: (info) => {
        const { source_lang, target_lang } = tableData[info?.row?.index || 0] || {source_lang: "", target_lang: "" };
        const search_lang = source_lang == "English" ? target_lang : source_lang;
        const translatorSearch = translators?.filter(( translator: any ) => {
          const languages = translator.languages;
          return languages.filter(( {language }:any ) => language === search_lang ).length > 0;
        })

        return (
          <Flex alignItems="center">
            <Menu>
              <MenuButton
                padding={"10px 10px 10px 15px"}
                backgroundColor="#FFF"
                borderRadius="100px"
                border="1px solid #E9E9E9"
              >
                <Flex alignItems={"center"}>
                  <Text
                    fontSize={"14px"}
                    lineHeight="24px"
                    fontWeight={"400"}
                    color="black"
                    maxW={{ md: "100px", base: "100%" }}
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                    overflow="hidden"
                  >
                    {info.getValue()?.length > 0
                      ? `${info.getValue()[0]?.first_name} ${
                          info.getValue()[0]?.last_name
                        }`
                      : "Not Assigned"}
                  </Text>
                  <IconBox
                    marginLeft="10px"
                    icon={<IconArrowDown width="24px" height="24px" />}
                  ></IconBox>
                </Flex>
              </MenuButton>
              <MenuList
                width="195px"
                minWidth="195px"
                boxShadow={shadow}
                maxH="300px"
                overflow="auto"
                p="0px"
                mt="10px"
                borderRadius="20px"
                bg={"#FFF"}
                border="none"
              >
                <Flex
                  flexDirection="column"
                  p="10px 20px"
                  fontSize={"14px"}
                  lineHeight="24px"
                  fontWeight={"400"}
                  color="black"
                >
                  {(translatorSearch.length > 0 ? translatorSearch : translators).map((option: any, index: number) => (
                    <MenuItem
                      key={index}
                      _hover={{ bg: "none" }}
                      _focus={{ bg: "none" }}
                      borderRadius="8px"
                      padding="10px 0px"
                      borderBottom={
                        index < translators.length - 1 ? "1px solid #F3F5F5" : ""
                      }
                      onClick={() =>
                        onAssignTranslator(info.row.original.document_id, option)
                      }
                    >
                      <Text fontSize="sm" className={ (info.getValue() ?? []).map(({ email }) => email).find(email => email === option.email) ? "blue-text" : "default-text"}>{`${option?.first_name ?? ""} ${
                        option?.last_name ?? ""
                      }`}</Text>
                      <Button
                        padding="5px 10px"
                        borderRadius="100px"
                        bgColor="#F3F5F5"
                        fontSize={"14px"}
                        lineHeight="24px"
                        fontWeight={"400"}
                        marginLeft="auto"
                      >
                        View
                      </Button>
                    </MenuItem>
                  ))}
                </Flex>
              </MenuList>
            </Menu>
          </Flex>
        );
      }
        
    }),

    columnHelper.accessor("actionCol", {
      id: "actionCol",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          color="gray.500"
          fontWeight="400"
        ></Text>
      ),
      cell: (info) => (
        <Flex justifyContent={"flex-end"} alignItems="center">
          <Button
            disabled={!info.row.original.hasChanged}
            padding="10px 20px"
            border={"2px solid #2F80FD"}
            borderRadius="100px"
            fontSize="14px"
            lineHeight="24px"
            fontWeight="400"
            color="#2F80FD"
            bg="#FFF"
            onClick={(e) => onClickSaveDocument(info.row.original.document_id)}
          >
            Save Changes
          </Button>
        </Flex>
      ),
    }),
  ];
  useEffect(() => {
    setData([...defaultData]);
  }, [JSON.stringify(defaultData)]);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const columnMinWidth: any = {
    language: 100,
    document_status_id: 220,
    file: 260,
    translators: 200,
    managers: 220,
    actionCol: 200,
  };

  return (
    <Card
      flexDirection="column"
      w="100%"
      p="0px"
      mb="40px"
      overflowX="auto"
      borderRadius="0"
      sx={{
        ".chakra-menu__menu-button + div": {
          position: "fixed !important",
        },
      }}
    >
      <Box w="100%">
        <Table
          w="100%"
          variant="simple"
          color="gray.500"
          pb="2"
          lineHeight="15px"
          borderRadius="0px"
        >
          <Thead display={{ md: "table-header-group", base: "none" }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index: number) => {
                  return (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      p="10px"
                      border={"none"}
                      fontFamily="Inter"
                      cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}
                      textTransform={"capitalize"}
                      position={
                        index === headerGroup.headers.length - 1
                          ? "sticky"
                          : "unset"
                      }
                      right={
                        index === headerGroup.headers.length - 1 ? "0" : "unset"
                      }
                      bgColor="#F1F1F1"
                    >
                      <Flex
                        justifyContent="space-between"
                        align="center"
                        sx={{
                          p: {
                            fontSize: "14px",
                            color: "#000",
                          },
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted() as string] ?? null}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody
            w="100%"
            display={{ md: "table-row-group", base: "flex" }}
            flexDirection="column"
          >
            {!isFetching &&
              table
                .getRowModel()
                .rows.slice(0, 11)
                .map((row) => {
                  return (
                    <Tr
                      key={row.id}
                      position="relative"
                      display={{ md: "table-row", base: "flex" }}
                      flexDirection="column"
                    >
                      {row.getVisibleCells().map((cell, index: number) => {
                        return (
                          <Td
                            padding="15px 10px"
                            key={cell.id}
                            fontSize={{ sm: "14px" }}
                            minW={{
                              base: `${columnMinWidth[cell.column.id]}px`,
                              md: `${columnMinWidth[cell.column.id]}px`,
                              lg: `${columnMinWidth[cell.column.id]}px`,
                            }}
                            position={
                              index === row.getVisibleCells().length - 1
                                ? "sticky"
                                : "unset"
                            }
                            right={
                              index === row.getVisibleCells().length - 1
                                ? "0"
                                : "unset"
                            }
                            bgGradient={
                              index === row.getVisibleCells().length - 1
                                ? "linear-gradient(90.91deg, rgba(255, 255, 255, 0.8) 0.73%, rgba(255, 255, 255, 0.77) 53.73%, rgba(255, 255, 255, 1) 98.58%);"
                                : "unset"
                            }
                            border="none"
                            width={{
                              md:
                                cell.column.id === "actionCol"
                                  ? "80px"
                                  : "auto",
                              base: "100%",
                            }}
                            borderBottom={{
                              md: "none !important",
                              base:
                                cell.column.id === "actionCol" &&
                                "1px solid #EEE !important",
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}

            {isFetching &&
              [1, 2].map((row) => {
                return (
                  <Fragment key={`skeleton-row-${row}`}>
                    <Tr zIndex="1" position="relative">
                      {columns.map((it: any, colIndex: number) => {
                        return (
                          <Td key={`skeleton-${colIndex}`} padding="20px 10px">
                            <Skeleton
                              height="25px"
                              colorScheme="#F3F5F5"
                              borderRadius="5px"
                            ></Skeleton>
                          </Td>
                        );
                      })}
                    </Tr>
                  </Fragment>
                );
              })}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
}
